import _ from 'lodash/fp'
import { all, call, put, select, take, takeLatest } from 'redux-saga/effects'
import { camelizeKeys, decamelizeKeys } from 'humps'
import gql from 'graphql-tag'

import * as authReducer from '../reducers/auth'
import { actions, selectors, types } from '../reducers/clubs'
import { getClubResources } from './auth'

import { assignLeft } from '@fe/redux/reducers/util'
import { getCurrentClubId, postEffect, prependClubsUrl } from './network'
import { apolloClient } from '@fe/business/src/apollo'

import { LOCATION_CHANGE } from 'connected-react-router'
import { navigateReplaceRoot } from './redirects'

import * as snack from './snackMessage'

import { invalidateKioskWidgetsFlow } from './kioskWidgets'

const decorateClub = _.flow(
  _.update('position.longitude', parseFloat),
  _.update('position.latitude', parseFloat),
  _.update('regionIds', ids => ids || [])
)

const CLUB_FIELDS = gql`
  fragment ClubFields on Club {
    id
    name
    handle
    themeName
    srid
    pro
    customer
    active
    isImperial
    position {
      longitude
      latitude
    }
    latitude
    longitude
    email
    phone
    websiteUrl
    bookingUrl
    logoFileName
    logoFileSize
    logoContentType
    logoUpdatedAt
    openingHoursCustom
    description
    localRules
    teaser
    price
    adminId
    managerId
    drawingStyleId
    createdAt
    updatedAt
    address {
      street
      zip
      city
      countryCode
    }
    openingHours {
      business
      enabled
      openingHours {
        day
        enabled
        open
        close
      }
    }
    orgs {
      id
      name
      orgClubId
    }
  }
`

const GET_CLUBS = gql`
  query GetClubs {
    ClubQuery {
      clubs {
        ...ClubFields
      }
    }
  }
  ${CLUB_FIELDS}
`

const GET_CLUB = gql`
  query GetClub($clubId: ID!) {
    ClubQuery {
      club(clubId: $clubId) {
        ...ClubFields
      }
    }
  }
  ${CLUB_FIELDS}
`

const UPDATE_CLUB = gql`
  mutation UpdateClub($input: UpdateClubInput!) {
    ClubMutation {
      updateClub(input: $input) {
        id
      }
    }
  }
`

const toReduxClub = (club) => {
  const withOpeningHours = _.has('openingHours', club)
    ? _.set('openingHours', fromGraphqlOpeningHours(club.openingHours), club)
    : club
  return decamelizeKeys(withOpeningHours)
}

const OPENING_HOURS_DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']

// GraphQL returns a list of businesses; the UI expects a map keyed by business name
const fromGraphqlOpeningHours = (entries) => {
  if (!_.isArray(entries)) {
    return undefined
  }

  return entries.reduce((acc, entry) => {
    if (!entry.business) {
      return acc
    }

    const days = (entry.openingHours || []).reduce((dayAcc, day) => {
      if (!day.day) {
        return dayAcc
      }
      dayAcc[day.day] = { open: day.open, close: day.close, enabled: day.enabled }
      return dayAcc
    }, {})
    acc[entry.business] = { enabled: entry.enabled, ...days }
    return acc
  }, {})
}

// Reverse of fromGraphqlOpeningHours, matching the nested openingHours list shape the mutation expects
const toGraphqlOpeningHours = (openingHoursMap) => {
  if (!_.isPlainObject(openingHoursMap)) {
    return undefined
  }

  return Object.keys(openingHoursMap).map((business) => {
    const data = openingHoursMap[business] || {}
    const openingHours = OPENING_HOURS_DAY_KEYS
      .filter((day) => _.isPlainObject(data[day]))
      .map((day) => ({
        day,
        enabled: Boolean(_.get('enabled', data[day])),
        open: _.get('open', data[day]) || null,
        close: _.get('close', data[day]) || null
      }))

    return {
      business,
      enabled: Boolean(data.enabled),
      openingHours
    }
  })
}

const stripTypenameDeep = (value) => {
  if (_.isArray(value)) {
    return _.map(stripTypenameDeep, value)
  }

  if (_.isObject(value)) {
    return _.flow(
      _.omit(['__typename', 'typename']),
      _.mapValues(stripTypenameDeep)
    )(value)
  }

  return value
}

const toGraphqlClubInput = (club) => {
  const normalizedClub = decorateClub(club)
  const camelizedClub = stripTypenameDeep(camelizeKeys(normalizedClub))
  const orgs = _.map(
    (org) => ({
      id: _.get('id', org),
      name: _.get('name', org),
      orgClubId: _.get('orgClubId', org)
    }),
    _.getOr([], 'orgs', camelizedClub)
  )

  const sridValue = _.get('srid', camelizedClub)
  const srid = _.isNil(sridValue) || sridValue === '' ? null : Number(sridValue)

  return {
    id: _.get('id', camelizedClub),
    name: _.get('name', camelizedClub),
    handle: _.get('handle', camelizedClub),
    themeName: _.get('themeName', camelizedClub),
    srid: Number.isFinite(srid) ? srid : null,
    pro: _.get('pro', camelizedClub),
    customer: _.get('customer', camelizedClub),
    active: _.get('active', camelizedClub),
    orgs,
    position: stripTypenameDeep(_.get('position', camelizedClub)),
    isImperial: _.get('isImperial', camelizedClub),
    address: _.get('address', camelizedClub),
    email: _.get('email', camelizedClub),
    phone: _.get('phone', camelizedClub),
    websiteUrl: _.get('websiteUrl', camelizedClub),
    bookingUrl: _.get('bookingUrl', camelizedClub),
    description: _.get('description', camelizedClub),
    localRules: _.get('localRules', camelizedClub),
    openingHours: toGraphqlOpeningHours(_.get('openingHours', camelizedClub)),
    openingHoursCustom: _.get('openingHoursCustom', camelizedClub),
    price: _.get('price', camelizedClub),
    teaser: _.get('teaser', camelizedClub)
  }
}

//  ███████╗███████╗███████╗███████╗ ██████╗████████╗███████╗
//  ██╔════╝██╔════╝██╔════╝██╔════╝██╔════╝╚══██╔══╝██╔════╝
//  █████╗  █████╗  █████╗  █████╗  ██║        ██║   ███████╗
//  ██╔══╝  ██╔══╝  ██╔══╝  ██╔══╝  ██║        ██║   ╚════██║
//  ███████╗██║     ██║     ███████╗╚██████╗   ██║   ███████║
//  ╚══════╝╚═╝     ╚═╝     ╚══════╝ ╚═════╝   ╚═╝   ╚══════╝

export function * loadClubEffect ({ clubId: optionalClubId } = {}) {
  try {
    const clubId = optionalClubId || (yield call(getCurrentClubId))
    const response = yield call([apolloClient, apolloClient.query], {
      query: GET_CLUB,
      variables: { clubId },
      fetchPolicy: 'network-only'
    })
    const club = _.get('data.ClubQuery.club', response)
    return club ? toReduxClub(club) : null
  } catch (error) {
    return { error }
  }
}

export function * loadClubsEffect () {
  try {
    const response = yield call([apolloClient, apolloClient.query], {
      query: GET_CLUBS,
      fetchPolicy: 'network-only'
    })
    return _.map(toReduxClub, _.getOr([], 'data.ClubQuery.clubs', response))
  } catch (error) {
    return { error }
  }
}

export function * putClubEffect (club) {
  try {
    const response = yield call([apolloClient, apolloClient.mutate], {
      mutation: UPDATE_CLUB,
      variables: {
        input: toGraphqlClubInput(club)
      }
    })
    return _.get('data.ClubMutation.updateClub', response)
  } catch (error) {
    return { error }
  }
}

export function * createClubEffect (club) {
  club = decorateClub(club)

  const request = {
    url: `clubs`,
    options: {
      body: {
        name: club.name,
        handle: club.handle,
        srid: club.srid,
        region_ids: club.regionIds,
        position: club.position,
        'active': false,
        'admin_id': 0,
        'address': {
          'street': '',
          'city': '',
          'zip': '',
          'country_code': 'dk'
        },
        'drawing_style_id': 6
      }
    }
  }
  const chan = yield call(postEffect, request)
  return yield take(chan)
}

//  ███████╗ █████╗  ██████╗  █████╗ ███████╗
//  ██╔════╝██╔══██╗██╔════╝ ██╔══██╗██╔════╝
//  ███████╗███████║██║  ███╗███████║███████╗
//  ╚════██║██╔══██║██║   ██║██╔══██║╚════██║
//  ███████║██║  ██║╚██████╔╝██║  ██║███████║
//  ╚══════╝╚═╝  ╚═╝ ╚═════╝ ╚═╝  ╚═╝╚══════╝

/**
 * takeLatest(types.LOAD_REQUEST, loadClubsFlow)
 */
export function * loadClubsFlow ({ payload }) {
  const clubs = yield call(loadClubsEffect, payload)
  if (clubs.error) {
    yield put(actions.loadError(clubs))
  } else {
    yield put(actions.loadSuccess(clubs))
  }
  yield put(actions.loadRequestDone())
}

export function * loadClubFlow ({ payload }) {
  const club = yield call(loadClubEffect, payload)
  if (club.error) {
    yield put(actions.loadError(club))
  } else if (!club) {
    yield put(actions.loadError({ error: 'Club not found' }))
  } else {
    yield put(actions.loadSuccess([club]))
  }
  yield put(actions.loadRequestDone())
}

export function * putClubFlow ({ payload }) {
  const club = yield select(selectors.getCurrentClub)
  const mergedClub = assignLeft(payload, club)
  const result = yield call(putClubEffect, mergedClub)

  if (!result.error) {
    yield put(actions.loadRequestSingle())
    yield take(types.LOAD_REQUEST_DONE)
    
    yield call(invalidateKioskWidgetsFlow, {
      clubId: _.get('id', club),
      widgetNames: ['club-info']
    })
  } else {
    yield snack.setMessage('Saving club has failed')
  }
  yield put(actions.doneSaving())
}

export function * switchClubFlow ({ payload }) {
  const { clubId, url = 'home' } = payload
  yield put(authReducer.actions.reloadProfileRequest())
  yield take([authReducer.types.RELOAD_PROFILE_SUCCESS, authReducer.types.RELOAD_PROFILE_ERROR])
  yield call(navigateReplaceRoot, `/${clubId}/${url}`)
  yield take(LOCATION_CHANGE)
  yield call(getClubResources)
}

export function * createClubFlow ({ payload }) {
  const result = yield call(createClubEffect, payload)
  const error = _.get('error', result)
  if (error) {
    yield put(actions.createError(error))
  } else {
    const clubId = _.get('id', result)
    yield put(actions.loadRequest())
    yield take(types.LOAD_REQUEST_DONE)
    yield put(actions.createSuccess(result))
    yield call(switchClubFlow, { payload: { clubId, url: 'club' } })
  }
}

export default function * () {
  return yield all([
    takeLatest(types.LOAD_REQUEST, loadClubsFlow),
    takeLatest(types.LOAD_REQUEST_SINGLE, loadClubFlow),
    takeLatest(types.SAVE_CLUB, putClubFlow),
    takeLatest(types.SWITCH_CLUB, switchClubFlow),

    takeLatest(types.CREATE_REQUEST, createClubFlow)
  ])
}
